import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { ApiError, apiFetch } from "../api/client";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard";
import { authEntryPathForError } from "../lib/authRedirect";

type Member = { user_id: string; email: string; role: string };
type PendingInvitation = {
  id: string;
  email: string;
  role: string;
  expires_at: string;
};
type ShareLink = {
  id: string;
  role: string;
  uses_count: number;
  max_uses: number | null;
  expires_at: string | null;
  revoked_at: string | null;
};

export function BoardSettingsPage() {
  const { id: boardId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [sub, setSub] = useState("Загрузка…");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [myUserId, setMyUserId] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [guestPolicy, setGuestPolicy] = useState("private");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState("editor");
  const [linkRole, setLinkRole] = useState("editor");
  const [linkMax, setLinkMax] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const newLinkInputRef = useRef<HTMLInputElement>(null);
  const linkCopy = useCopyToClipboard();

  useEffect(() => {
    linkCopy.reset();
  }, [newLinkUrl, linkCopy.reset]);

  useEffect(() => {
    if (!boardId) {
      navigate("/cabinet", { replace: true });
      return;
    }
    void (async () => {
      try {
        const [me, boardData, membersData] = await Promise.all([
          apiFetch<{ id: string }>("/api/v1/me"),
          apiFetch<{ board: { title: string; guest_policy?: string } }>(
            `/api/v1/boards/${boardId}`
          ),
          apiFetch<{ owner: { user_id: string; email: string }; members: Member[]; pending_invitations?: PendingInvitation[] }>(
            `/api/v1/boards/${boardId}/members`
          ),
        ]);
        const ownerMode = membersData.owner.user_id === me.id;
        setMyUserId(me.id);
        setIsOwner(ownerMode);
        setIsMember(!ownerMode && (membersData.members || []).some((m) => m.user_id === me.id));
        setTitle(boardData.board.title || "Доска");
        setOwnerEmail(membersData.owner.email);
        setMembers(membersData.members || []);
        setPendingInvitations(membersData.pending_invitations || []);
        setGuestPolicy(boardData.board.guest_policy || "private");
        setSub(ownerMode ? `ID ${boardId} · вы владелец` : "Только просмотр участников");
        if (ownerMode) await loadLinks();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          navigate(authEntryPathForError(e, location.pathname), { replace: true });
          return;
        }
        setErr(e instanceof Error ? e.message : "Ошибка загрузки");
        setSub("");
      }
    })();
  }, [boardId, navigate]);

  async function loadLinks() {
    if (!boardId) return;
    const data = await apiFetch<{ shareLinks: ShareLink[] }>(
      `/api/v1/boards/${boardId}/share-links`
    );
    setLinks(data.shareLinks || []);
  }

  async function reloadMembers() {
    if (!boardId) return;
    const m = await apiFetch<{
      members: Member[];
      pending_invitations?: PendingInvitation[];
    }>(`/api/v1/boards/${boardId}/members`);
    setMembers(m.members || []);
    setPendingInvitations(m.pending_invitations || []);
  }

  async function addMember(e: FormEvent) {
    e.preventDefault();
    if (!boardId) return;
    try {
      const res = await apiFetch<{ member?: Member; invitation?: PendingInvitation & { mail_sent?: boolean } }>(
        `/api/v1/boards/${boardId}/members`,
        { method: "POST", json: { email: memberEmail.trim(), role: memberRole } }
      );
      setMemberEmail("");
      if (res.invitation) {
        setOk(
          res.invitation.mail_sent
            ? "Приглашение отправлено на email"
            : "Приглашение создано (SMTP не настроен — передайте ссылку вручную)"
        );
      } else {
        setOk("Участник добавлен");
      }
      await reloadMembers();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function createLink(e: FormEvent) {
    e.preventDefault();
    if (!boardId) return;
    try {
      const body: { role: string; max_uses?: number } = { role: linkRole };
      if (linkMax.trim()) body.max_uses = Number(linkMax);
      const data = await apiFetch<{ shareLink: { openPath?: string } }>(
        `/api/v1/boards/${boardId}/share-links`,
        { method: "POST", json: body }
      );
      setNewLinkUrl(`${location.origin}${data.shareLink.openPath || ""}`);
      setLinkMax("");
      setOk("Ссылка создана");
      await loadLinks();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  const visibleLinks = activeOnly ? links.filter((l) => !l.revoked_at) : links;

  return (
    <>
      <SiteHeader
        right={
          <>
            <Link className="wb-btn wb-btn--ghost wb-btn--sm" to="/cabinet">
              Кабинет
            </Link>
            <Link className="wb-btn wb-btn--ghost wb-btn--sm" to={`/board/${boardId}`}>
              К доске
            </Link>
          </>
        }
      />
      <div className="wb-shell wb-shell--narrow">
        {err ? <p className="wb-alert wb-alert--error">{err}</p> : null}
        {ok ? <p className="wb-alert wb-alert--ok">{ok}</p> : null}
        <h1 className="wb-h1">Доступ: {title}</h1>
        <p className="wb-muted">{sub}</p>

        <section className="wb-panel wb-settings-block">
          <h2 className="wb-h2">Участники</h2>
          <p className="wb-settings-hint">
            Пользователи с аккаунтом на этом сайте. Редакторы могут менять доску без владельца
            онлайн. Если email ещё не зарегистрирован — отправится приглашение.
          </p>
          <ul className="wb-settings-list">
            <li className="wb-settings-list__item">
              <span>
                <strong>{ownerEmail}</strong> · владелец
              </span>
            </li>
            {members.map((m) => (
              <li key={m.user_id} className="wb-settings-list__item">
                <span>{m.email}</span>
                {isOwner ? (
                  <div className="wb-settings-list__actions">
                    <select
                      className="wb-select wb-select--sm"
                      value={m.role}
                      aria-label={`Роль ${m.email}`}
                      onChange={(e) => {
                        const role = e.target.value;
                        void apiFetch(`/api/v1/boards/${boardId}/members/${m.user_id}`, {
                          method: "PATCH",
                          json: { role },
                        })
                          .then(() => {
                            setOk("Роль обновлена");
                            void reloadMembers();
                          })
                          .catch((ex) =>
                            setErr(ex instanceof Error ? ex.message : "Ошибка")
                          );
                      }}
                    >
                      <option value="editor">Редактор</option>
                      <option value="viewer">Только просмотр</option>
                    </select>
                    <button
                      type="button"
                      className="wb-btn wb-btn--danger wb-btn--sm"
                      onClick={() => {
                        if (!confirm(`Удалить ${m.email}?`)) return;
                        void apiFetch(`/api/v1/boards/${boardId}/members/${m.user_id}`, {
                          method: "DELETE",
                        }).then(async () => {
                          setOk("Участник удалён");
                          await reloadMembers();
                        });
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                ) : (
                  <span className="wb-muted">{m.role}</span>
                )}
              </li>
            ))}
            {pendingInvitations.map((inv) => (
              <li key={inv.id} className="wb-settings-list__item">
                <span>
                  {inv.email} · <span className="wb-muted">ожидает ({inv.role})</span>
                </span>
                {isOwner ? (
                  <button
                    type="button"
                    className="wb-btn wb-btn--danger wb-btn--sm"
                    onClick={() => {
                      void apiFetch(`/api/v1/boards/${boardId}/invitations/${inv.id}`, {
                        method: "DELETE",
                      }).then(async () => {
                        setOk("Приглашение отозвано");
                        await reloadMembers();
                      });
                    }}
                  >
                    Отозвать
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {isOwner ? (
            <form className="wb-form-stack wb-settings-form" onSubmit={addMember}>
              <div>
                <label className="wb-label" htmlFor="member-email">
                  Email участника
                </label>
                <input
                  id="member-email"
                  className="wb-input"
                  type="email"
                  required
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="wb-label" htmlFor="member-role">
                  Роль
                </label>
                <select
                  id="member-role"
                  className="wb-select"
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value)}
                >
                  <option value="editor">Редактор</option>
                  <option value="viewer">Только просмотр</option>
                </select>
              </div>
              <button type="submit" className="wb-btn wb-btn--primary">
                Добавить участника
              </button>
            </form>
          ) : null}
        </section>

        {isOwner ? (
          <>
            <section className="wb-panel wb-settings-block">
              <h2 className="wb-h2">Гости без регистрации</h2>
              <p className="wb-settings-hint">
                При политике «по ссылке» гость обменивает share-ссылку на cookie-сессию (без{" "}
                <code>?t=</code> в URL). При «только участники» токен остаётся в адресе.
              </p>
              <form
                className="wb-form-stack wb-settings-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void apiFetch(`/api/v1/boards/${boardId}`, {
                    method: "PATCH",
                    json: { guest_policy: guestPolicy },
                  }).then(() => setOk("Политика гостей сохранена"));
                }}
              >
                <select
                  className="wb-select"
                  value={guestPolicy}
                  onChange={(e) => setGuestPolicy(e.target.value)}
                >
                  <option value="private">Только участники (токен в URL)</option>
                  <option value="link_read">По ссылке — cookie-сессия</option>
                </select>
                <button type="submit" className="wb-btn wb-btn--primary">
                  Сохранить
                </button>
              </form>
            </section>

            <section className="wb-panel wb-settings-block">
              <h2 className="wb-h2">Ссылки для входа</h2>
              <p className="wb-settings-hint">
                Можно раздать без регистрации. Ссылка с ролью «редактор» — совместная работа;
                «просмотр» — только курсоры и просмотр.
              </p>
              <label className="wb-settings-inline">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => {
                    setActiveOnly(e.target.checked);
                    void loadLinks();
                  }}
                />
                Только активные ссылки
              </label>
              <ul className="wb-settings-list">
                {visibleLinks.length === 0 ? (
                  <li className="wb-settings-empty">Активных ссылок пока нет.</li>
                ) : null}
                {visibleLinks.map((l) => (
                  <li key={l.id} className="wb-settings-list__item">
                    <span>
                      {l.role === "editor" ? "Редактор" : "Просмотр"} · {l.uses_count}
                      {l.max_uses != null ? ` / ${l.max_uses}` : " исп."}
                      {l.expires_at
                        ? ` · до ${new Date(l.expires_at).toLocaleDateString("ru-RU")}`
                        : ""}
                      {l.revoked_at ? " · отозвана" : ""}
                    </span>
                    {!l.revoked_at ? (
                      <button
                        type="button"
                        className="wb-btn wb-btn--danger wb-btn--sm"
                        onClick={() => {
                          if (!confirm("Отозвать ссылку?")) return;
                          void apiFetch(`/api/v1/boards/${boardId}/share-links/${l.id}`, {
                            method: "DELETE",
                          }).then(() => loadLinks());
                        }}
                      >
                        Отозвать
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
              <form className="wb-form-stack wb-settings-form" onSubmit={createLink}>
                <div>
                  <label className="wb-label" htmlFor="link-role">
                    Роль по ссылке
                  </label>
                  <select
                    id="link-role"
                    className="wb-select"
                    value={linkRole}
                    onChange={(e) => setLinkRole(e.target.value)}
                  >
                    <option value="editor">Редактор (без аккаунта)</option>
                    <option value="viewer">Только просмотр</option>
                  </select>
                </div>
                <div>
                  <label className="wb-label" htmlFor="link-max">
                    Лимит использований (необязательно)
                  </label>
                  <input
                    id="link-max"
                    className="wb-input"
                    type="number"
                    min={1}
                    placeholder="Без лимита"
                    value={linkMax}
                    onChange={(e) => setLinkMax(e.target.value)}
                  />
                </div>
                <button type="submit" className="wb-btn wb-btn--primary">
                  Создать ссылку
                </button>
              </form>
              {newLinkUrl ? (
                <div className="wb-new-link">
                  <p className="wb-label">
                    Новая ссылка (скопируйте сейчас — токен больше не покажем):
                  </p>
                  <input ref={newLinkInputRef} className="wb-input" readOnly value={newLinkUrl} />
                  <button
                    type="button"
                    className={`wb-btn wb-btn--ghost wb-btn--sm${linkCopy.copied ? " is-copied" : ""}${linkCopy.error ? " is-error" : ""}`}
                    onClick={() => void linkCopy.copy(newLinkUrl, newLinkInputRef.current)}
                  >
                    {linkCopy.buttonLabel}
                  </button>
                  {linkCopy.copied ? (
                    <p className="wb-cabinet-toast wb-cabinet-toast--ok" role="status">
                      Ссылка скопирована в буфер обмена
                    </p>
                  ) : null}
                  {linkCopy.error ? (
                    <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
                      Не удалось скопировать — выделите ссылку и нажмите Ctrl+C
                    </p>
                  ) : null}
                </div>
              ) : null}
            </section>
          </>
        ) : null}

        {isMember && !isOwner ? (
          <section className="wb-panel wb-settings-block">
            <h2 className="wb-h2">Покинуть доску</h2>
            <p className="wb-settings-hint">
              Вы перестанете видеть эту доску в кабинете. Доступ через папку не затрагивается.
            </p>
            <button
              type="button"
              className="wb-btn wb-btn--danger"
              onClick={() => {
                if (!boardId || !myUserId) return;
                if (!confirm("Покинуть доску?")) return;
                void apiFetch(`/api/v1/boards/${boardId}/members/${myUserId}`, {
                  method: "DELETE",
                }).then(() => navigate("/cabinet", { replace: true }));
              }}
            >
              Покинуть доску
            </button>
          </section>
        ) : null}
      </div>
    </>
  );
}
